import { useDebounceFn } from '@vueuse/core';
import type { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type Style from 'ol/style/Style';

import type { DrawLayerResponseDTO, GeoJsonFeatureCollectionDTO, GeoJsonFeatureDTO } from '@nora-monorepo/nora-api-client';

import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import { BBOX_RELOAD_DEBOUNCE_MS, BBOX_RELOAD_MAX_WAIT_MS } from '#nora-ui/map/map/constants';
import { NoraLayerManager } from '#nora-ui/map/map/layer-managers/NoraLayerManager';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';

export class DrawLayerManager extends NoraLayerManager {
  readonly LAYER_ID = 'drawLayer';
  readonly LAYER_NAME = 'NORA Drawings';
  override readonly isClickable = true;

  private BASE_Z_INDEX = 1000;

  /**
   * Spacing between the style-zIndex buckets of two adjacent draw layers. Must exceed the
   * highest zIndex any individual style function uses (labels use 1000), so a feature's
   * internal style stacking stays intact within its layer's bucket.
   */
  private static readonly LAYER_ORDER_Z_SCALE = 100_000;

  /**
   * Original zIndex of each style before applyLayerOrderZIndex rescaled it. Some style
   * functions (e.g. the ruler) return shared module-level Style instances, so rescaling
   * from the *current* zIndex would compound across features.
   */
  private originalStyleZIndexes = new WeakMap<Style, number>();

  protected source: VectorSource;
  protected layer: VectorLayer<VectorSource>;

  private debouncedLoadCurrentBbox = useDebounceFn(async (): Promise<void> => {
    await this.map.state.draw.loadCurrentBbox();
  }, BBOX_RELOAD_DEBOUNCE_MS, {
    maxWait: BBOX_RELOAD_MAX_WAIT_MS,
  });

  constructor(private readonly map: NoraMap) {
    super();

    this.source = new VectorSource();
    this.layer = new VectorLayer({
      source: this.source,
      style: feature => this.applyLayerOrderZIndex(feature, getFeatureStyle(feature)),
    });
  }

  /**
   * Lifts every style of a feature into a style-zIndex bucket derived from the per-feature
   * `zIndex` property that recalculateOperationDrawLayerZIndex assigns from the draw layer
   * order. Style zIndex is the only ordering OpenLayers applies across builder types —
   * within one zIndex it always paints fills before icons before text, regardless of
   * feature order — so bucketing per draw layer is what makes "layer A above layer B"
   * hold for mixed feature types.
   */
  private applyLayerOrderZIndex(feature: FeatureLike, styles: Style | Style[]): Style | Style[] {
    const layerOrderZIndex = (feature.get('zIndex') as number | undefined) ?? 0;
    const styleArray = Array.isArray(styles) ? styles : [styles];

    for (const style of styleArray) {
      let originalZIndex = this.originalStyleZIndexes.get(style);

      if (originalZIndex === undefined) {
        originalZIndex = style.getZIndex() ?? 0;
        this.originalStyleZIndexes.set(style, originalZIndex);
      }

      style.setZIndex(layerOrderZIndex * DrawLayerManager.LAYER_ORDER_Z_SCALE + originalZIndex);
    }

    return styles;
  }

  override getLayer(): VectorLayer<VectorSource> {
    return this.layer;
  }

  override async setup(): Promise<void> {
    await this.setupPanHandler();
  }

  /**
   * Load/reload draw layer features from state.
   * Clears existing features before loading.
   */
  override async load(): Promise<void> {
    this.unload();
    this.loadDrawFeatures();
  }

  /**
   * Unload/clear all draw layer features.
   */
  override unload(): void {
    this.source.clear();
  }

  setDrawLayerVisibility(drawLayerId: string, isVisible: boolean): void {
    if (isVisible) {
      this.showDrawLayer(drawLayerId);
    }
    else {
      this.hideDrawLayer(drawLayerId);
    }
  }

  showDrawLayer(drawLayerId: string): void {
    this.source.forEachFeature((feature) => {
      if (feature.get('drawLayerId') === drawLayerId) {
        feature.unset('hidden');
      }
    });
  }

  hideDrawLayer(drawLayerId: string): void {
    this.source.forEachFeature((feature) => {
      if (feature.get('drawLayerId') === drawLayerId) {
        feature.set('hidden', true);
      }
    });
  }

  recalculateOperationDrawLayerZIndex(): void {
    const drawLayers = this.map.state.draw.drawLayers;

    // Index the target zIndex per draw layer once, then walk the source a single time
    const zIndexByDrawLayerId = new Map<string, number>();

    drawLayers.forEach((drawLayer, index) => {
      zIndexByDrawLayerId.set(drawLayer.id, this.BASE_Z_INDEX - index);
    });

    this.source.forEachFeature((feature) => {
      const drawLayerId = feature.get('drawLayerId') as string | undefined;

      if (drawLayerId === undefined) {
        return;
      }

      const zIndex = zIndexByDrawLayerId.get(drawLayerId);

      if (zIndex !== undefined) {
        feature.set('zIndex', zIndex);
      }
    });
  }

  private async setupPanHandler(): Promise<void> {
    this.map.getOlMapOrThrow().on('moveend', async () => {
      await this.debouncedLoadCurrentBbox();
    });
  }

  private loadDrawFeatures(): void {
    const layers = this.map.state.draw.drawLayers;

    layers.forEach((drawLayer) => {
      const geoJson = drawLayer.geoJson;

      if (!geoJson) {
        return;
      }

      this.addFeatures(drawLayer, geoJson);
    });

    this.recalculateOperationDrawLayerZIndex();
  }

  private addFeatures(drawLayer: DrawLayerResponseDTO, features: GeoJsonFeatureCollectionDTO): void {
    const geoJson = this.createDrawLayerGeoJson(drawLayer, features);

    this.source.addFeatures(
      new GeoJSON().readFeatures(geoJson, {
        dataProjection: MapSetup.WGS84,
        featureProjection: MapSetup.WEB_MERCATOR,
      }),
    );
  }

  private createDrawLayerGeoJson(drawLayer: DrawLayerResponseDTO, collection: GeoJsonFeatureCollectionDTO): GeoJsonFeatureCollectionDTO {
    // feature id of the original feature we want to edit
    const editingFeatureId = this.map.drawToolUI.getEditingFeatureId();

    // When we edit, we exclude the original feature id to prevent it from appearing if a new bbox call is sent
    const newFeatures = collection.features
      .filter(feature => feature.id !== editingFeatureId)
      .map((feature) => {
        return this.transformFeatureForDrawLayer(drawLayer, feature);
      });

    return {
      ...collection,
      features: newFeatures,
    };
  }

  private transformFeatureForDrawLayer(drawLayer: DrawLayerResponseDTO, feature: GeoJsonFeatureDTO) {
    const { id, ...featureData } = feature;

    const isDrawLayerVisible = this.map.state.draw.isDrawLayerVisible(drawLayer.id);

    return {
      ...featureData,
      id,
      properties: {
        ...feature.properties,
        id,
        drawLayerId: drawLayer.id,
        hidden: !isDrawLayerVisible,
      },
    };
  }
}
